import logoUrl from "@/assets/instance/rainbow-logo.png"

/**
 * INSTANCE CONFIGURATION
 *
 * The words and marks this deployment calls itself. The same rule as the
 * brand block in `styles.css`: branding is instance configuration by design
 * (decision 8), so the product name and logo live in this one slot and
 * nowhere else. A second client is this file, its logo asset and the CSS
 * block, not a search through the screens.
 *
 * The logo is the client's own artwork, extracted from their CI guideline
 * rather than recreated: the guideline's Logo Usage page forbids changing
 * the font or the mark, and a recreation is exactly that.
 *
 * M5's white-label packaging turns these into deployment configuration;
 * until then this file is where they are set.
 */
export const INSTANCE = {
  name: "Rainbow TMS",
  tagline: "Training Management",
  /** The wordmark, on white per the CI's correct-usage rules. */
  logo: logoUrl,
  logoAlt: "Rainbow",
} as const

/**
 * What this deployment calls each role (ruling R8, 2026-08-04).
 *
 * Keyed by the **internal group identifier**, which never changes: every
 * permission check, every seeded group and every API response still says
 * `Coordinator`, and `lib\session.ts` declares those identifiers. Only the
 * word on the screen is configurable, because the client names the role
 * "Manager" and the next client will name it something else again. That is
 * the same rule as the product name above: vocabulary is instance
 * configuration, semantics are not.
 *
 * Add a key here when the server grows a group; an unlisted one still renders
 * (see `roleDisplayName`) rather than leaving a blank where a role should be.
 */
export const ROLE_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  Administrator: "Administrator",
  Coordinator: "Manager",
  Viewer: "Viewer",
}

/**
 * The word to show for a role, given its internal identifier.
 *
 * A group with no entry above is title-cased rather than dropped: the screens
 * render whatever the server says the account holds, so a group added on the
 * server before this file hears about it must still read as something, and a
 * missing role word looks exactly like an account with no role at all.
 */
export function roleDisplayName(role: string): string {
  const configured = ROLE_DISPLAY_NAMES[role]
  if (configured) return configured
  return role
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

/**
 * A role named inside a sentence, with the article its own word needs.
 *
 * "Only an Administrator may sign off" is grammatical here and is not at a
 * client who calls that role Manager, so the article has to follow the
 * configured word rather than the one the sentence was written with. The rule
 * is the written vowel, which is right for every role word we can foresee;
 * the exceptions English keeps for this (a European, an hour) would need the
 * spoken sound, and are not worth carrying until a client's role name needs
 * one.
 */
export function roleWithArticle(role: string): string {
  const word = roleDisplayName(role)
  if (!word) return ""
  return `${/^[aeiou]/i.test(word) ? "an" : "a"} ${word}`
}
